import React, { useEffect, useState, useRef, useContext } from 'react';
import classNames from 'classnames';
import EpiserverFormsBlock from '../EpiserverFormsBlock';
import SurveyResult from './survey-result';
import Spinner from '../Spinner';
import { createCookie, readCookie } from '../Base/Cookies';
import { CookieContext } from '../Context/cookies';
import useWindowLocationHref from '../Hooks/useWindowLocation';

export default function Survey(props) {
  const formContainer = useRef(null);
  // TODO: Maybe refactor to use useReducer?
  const [isAnswered, setIsAnswered] = useState(!props.IsActive);
  const [peekResult, setPeekResult] = useState(false);
  const isAnweredNow = useRef(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState(null);
  const [isLoadingResult, setIsLoadingResult] = useState('PENDING');
  const cookieContext = useContext(CookieContext);
  const href = useWindowLocationHref();
  const surveyCookie = JSON.parse(readCookie('survey'));
  const alreadyAnswered = JSON.stringify(surveyCookie).includes(props.BlockId);

  const setLoading = (status) => async () => {
    setIsLoadingResult(status);
    return true;
  };

  function toggleResult() {
    setPeekResult(!peekResult);
    setIsAnswered(false);
  }

  function showResult() {
    if (isLoadingResult === 'LOADING') {
      return false;
    }
    return errors === null && result && (peekResult || isAnswered);
  }

  function fetchResult() {
    if (isLoadingResult === 'LOADING') {
      return false;
    }
    const endpoint = `/api/survey/result/?id=${props.BlockId}`;

    return fetch(endpoint)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Could not load results');
      })
      .then((json) => {
        setResult(json);
        setPeekResult(true);
      });
  }

  function handleResponse(res) {
    if (res.ok) {
      isAnweredNow.current = true;
      setIsAnswered(true);
      return true;
    }
    return Promise.reject(res);
  }

  function handleErrors(error) {
    console.warn(error);
    setErrors(`Error. ${error.status ? `(${status})` : ''}`);
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (alreadyAnswered) {
      validateForm();
      return;
    }

    if (validateForm() || (!validateForm() && alreadyAnswered)) {
      return;
    }

    const options = {
      method: 'POST',
      body: new FormData(event.target),
    };

    function postData() {
      return fetch(href ? href : '/', options);
    }

    setLoading('LOADING')()
      .then(postData)
      .then(handleResponse)
      .then(fetchResult)
      .then((res) => {
        if (cookieContext.isAllowed('Survey')) {
          const surveyCookieValue = JSON.parse(readCookie('survey'));

          if (!surveyCookieValue && surveyCookieValue === null) {
            let newCookie = JSON.stringify([props.BlockId]);
            createCookie('survey', newCookie, 1);
          }

          if (surveyCookieValue && !alreadyAnswered) {
            let newValue = JSON.stringify([
              ...surveyCookieValue,
              props.BlockId,
            ]);
            createCookie('survey', newValue, 1);
          }
        }

        return res;
      })
      .catch(handleErrors)
      .finally(setLoading('DONE'));
  }

  // If the question is answered before and result is not loaded
  // Go and fetch the result.
  // NOTE: Should not be used if we have the result in __PRELOADED_STATE__
  useEffect(() => {
    if (
      !isAnweredNow.current &&
      !result &&
      (isAnswered || peekResult) &&
      isLoadingResult === 'PENDING'
    ) {
      setLoading('LOADING')()
        .then(fetchResult)
        .catch(handleErrors)
        .finally(setLoading('DONE'));
    }
  }, [result, peekResult, isAnswered, isLoadingResult]);

  useEffect(() => {
    const elements = getFormInputs();
    elements.forEach((element) => {
      element.addEventListener('change', changeHandler);
    });
    return () => {
      elements.forEach((element) => {
        element.removeEventListener('change', changeHandler);
      });
    };
  });

  const getFormInputs = () => {
    if (formContainer.current === null) return [];
    const radio = Array.from(
      formContainer.current.querySelectorAll('input[type="radio"]')
    );
    const checkbox = Array.from(
      formContainer.current.querySelectorAll('input[type="checkbox"')
    );
    return [...radio, ...checkbox];
  };

  const changeHandler = () => {
    getFormInputs().forEach((element) => {
      if (element.checked) {
        element.parentElement.classList.add('checked');
      } else {
        element.parentElement.classList.remove('checked');
      }
    });
  };

  const validateForm = () => {
    const answerIsSelected = getFormInputs().filter(
      (inputfield) => inputfield.checked
    );
    if (answerIsSelected.length === 0 || alreadyAnswered) {
      //Dont create element if there's already an errordiv
      if (!formContainer.current.querySelector('.c-survey__error')) {
        let fieldset = formContainer.current.querySelector('fieldset');

        const errorMsg = document.createElement('div');
        errorMsg.className = 'c-survey__error';

        const errorIcon = document.createElement('div');
        errorIcon.className = 'c-survey__error__icon';

        const closeBtn = document.createElement('button');
        closeBtn.onclick = () => errorMsg.remove();

        const srText = document.createElement('span');
        srText.className = 'show-for-sr';
        let srTextNode = document.createTextNode(props.Translations.close);

        const textContainer = document.createElement('div');
        const textMsg = alreadyAnswered
          ? props.ElementsArea[0].Translations.alreadyanswered
          : props.ElementsArea[0].Translations.noanswerselected;
        let textnode = document.createTextNode(textMsg);

        srText.appendChild(srTextNode);
        textContainer.appendChild(errorIcon);
        textContainer.appendChild(textnode);
        errorMsg.appendChild(textContainer);
        closeBtn.appendChild(srText);
        errorMsg.appendChild(closeBtn);
        fieldset.appendChild(errorMsg);
      }
    } else {
      if (formContainer.current.querySelector('.c-survey__error')) {
        formContainer.current.querySelector('.c-survey__error').remove();
      }
    }

    return answerIsSelected.length === 0;
  };

  return (
    <div
      className={classNames({
        'c-survey': true,
        'c-survey--is-multiselect': props.ElementsArea[0].AllowMultiSelect,
        'c-survey--is-not-multiselect': !props.ElementsArea[0].AllowMultiSelect,
      })}
      ref={formContainer}
      data-cy="survey"
      data-survey-active={props.IsActive}
    >
      <header className="c-survey__header">
        {props.SurveyLabel && (
          <h2 className="c-survey__title">{props.SurveyLabel}</h2>
        )}
        <h3 className="c-survey__question">{props.Question}</h3>
      </header>

      {errors && <p>{errors}</p>}

      <div className="c-survey__main">
        {showResult() ? (
          <SurveyResult
            {...props}
            result={result}
            toggle={toggleResult}
            resultMeterColors={
              props.resultMeterColors
                ? props.resultMeterColors
                : ['#f01e00', '#57b2d9', '#96b92c', '#000000']
            }
          />
        ) : isLoadingResult === 'LOADING' ? (
          <div className="c-forms" style={{ minHeight: '100px' }}>
            <Spinner alt />
          </div>
        ) : (
          <EpiserverFormsBlock onSubmit={handleSubmit} {...props} />
        )}
        <footer className="c-survey__footer">
          {props.ResultLinkText &&
            props.IsActive &&
            !peekResult &&
            !isAnswered && (
              <button
                onClick={toggleResult}
                className={classNames({
                  'c-survey__link': true,
                })}
                data-cy="survey_show-result-btn"
              >
                <span>{props.ResultLinkText}</span>
              </button>
            )}
          {props.ArchiveLinkText && (
            <a className="c-survey__link" href={props.ArchivePage}>
              <span>{props.ArchiveLinkText}</span>
            </a>
          )}
        </footer>
      </div>
    </div>
  );
}
